import {
  Typography,
  Box,
  Card,
  CardContent,
  Container,
  alpha,
  useTheme,
  Chip,
  Button,
  Alert,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getQuiz, clearQuiz, getQuizResults } from "../../state/slices/quiz";
import { getCompletedAttemptByQuiz } from "../../state/slices/quizAttempt";
import { useSelector, useDispatch } from "react-redux";
import Loading from "../../components/loading/loading";
import { PlayArrow, Edit, Visibility, Quiz, ArrowBack, HelpOutline, AccessTime, CheckCircle, Person, Score, Pending } from "@mui/icons-material";
import dayjs from "dayjs";

// Question type labels mapping
const getQuestionTypeLabel = (type, t) => {
  const labels = {
    multipleChoice: t('quiz.questionTypeMultipleChoice'),
    trueFalse: t('quiz.questionTypeTrueFalse'),
    shortAnswer: t('quiz.questionTypeShortAnswer'),
    essay: t('quiz.questionTypeEssay'),
  };
  return labels[type] || type;
};

const QuizDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const { t } = useTranslation();
  const { currentQuiz, loading, quizResults } = useSelector((state) => state.quiz);
  const { completedAttempt } = useSelector((state) => state.quizAttempt);
  const user = useSelector((state) => state.user.user);
  const isTutor = user?.roles?.includes("TUTOR");
  const isStudent = user?.roles?.includes("STUDENT") || user?.roles?.includes("PARENT");
  const isCreator = currentQuiz?.createdBy?._id === user?.id || currentQuiz?.createdBy === user?.id;
  const [showResults, setShowResults] = useState(false);
  
  // Root navigation paths - don't show back button on these
  const rootPaths = ['/quiz/my', '/quiz/student', '/flashcard/my', '/flashcard/student', '/progress', '/', '/request', '/student-request', '/tutor-class-room', '/student-class-room'];
  const showBackButton = !rootPaths.includes(location.pathname);

  useEffect(() => {
    dispatch(getQuiz(id));
    if (isStudent) {
      dispatch(getCompletedAttemptByQuiz(id));
    }
    return () => {
      dispatch(clearQuiz());
    };
  }, [id, dispatch, isStudent]);

  useEffect(() => {
    if (isCreator && showResults && !quizResults) {
      dispatch(getQuizResults(id));
    }
  }, [id, dispatch, isCreator, showResults, quizResults]);

  if (loading || !currentQuiz) {
    return <Loading />;
  }

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 64px)",
        bgcolor: alpha(theme.palette.primary.main, 0.03),
        pb: 4,
      }}
    >
      <Container maxWidth="lg" sx={{ pt: 4 }}>
        {/* Back Button - only show if not on root navigation page */}
        {showBackButton && (
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate(-1)}
            sx={{
              mb: 3,
              color: 'text.secondary',
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.1),
              },
            }}
          >
            {t('common.back')}
          </Button>
        )}

        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            bgcolor: 'white',
            boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
            overflow: 'hidden',
          }}
        >
          {/* Header Section */}
          <Box
            sx={{
              p: 4,
              bgcolor: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexGrow: 1 }}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    bgcolor: theme.palette.primary.main,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Quiz sx={{ fontSize: 32 }} />
                </Box>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.primary.main, mb: 1 }}>
                    {currentQuiz.title}
                  </Typography>
                  {currentQuiz.description && (
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                      {currentQuiz.description}
                    </Typography>
                  )}
                  <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
                    {currentQuiz.subject && (
                      <Chip 
                        label={currentQuiz.subject} 
                        sx={{ 
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                          fontWeight: 600,
                        }} 
                      />
                    )}
                    {currentQuiz.gradeLevel && (
                      <Chip 
                        label={currentQuiz.gradeLevel}
                        sx={{ 
                          bgcolor: alpha(theme.palette.secondary.main, 0.1),
                          color: theme.palette.secondary.main,
                          fontWeight: 600,
                        }}
                      />
                    )}
                    <Chip 
                      label={currentQuiz.status} 
                      color="primary"
                      sx={{ fontWeight: 600 }}
                    />
                  </Stack>
                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <HelpOutline sx={{ fontSize: 18, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {currentQuiz.questions?.length || 0} {t('quiz.questions')}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircle sx={{ fontSize: 18, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {currentQuiz.totalPoints} {t('quiz.totalPoints')}
                      </Typography>
                    </Box>
                    {currentQuiz.timeLimit && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AccessTime sx={{ fontSize: 18, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {currentQuiz.timeLimit} {t('quiz.minutes')}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
              <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
                {/* Only show Take Quiz/View Results buttons for students/parents */}
                {isStudent && (
                  completedAttempt ? (
                    <Button
                      variant="contained"
                      startIcon={<Visibility />}
                      onClick={() => navigate(`/quiz/results/${completedAttempt._id}`)}
                      sx={{
                        borderRadius: 2,
                        px: 3,
                        py: 1.5,
                        fontWeight: 600,
                        boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.3)}`,
                      }}
                    >
                      {t('quiz.viewResults')}
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      startIcon={<PlayArrow />}
                      onClick={() => navigate(`/quiz/take/${id}`)}
                      disabled={currentQuiz.status !== 'published'}
                      sx={{
                        borderRadius: 2,
                        px: 3,
                        py: 1.5,
                        fontWeight: 600,
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                      }}
                    >
                      {t('quiz.takeQuiz')}
                    </Button>
                  )
                )}
                {isCreator && (
                  <Button
                    variant="outlined"
                    startIcon={<Edit />}
                    onClick={() => navigate(`/quiz/create`, { state: { quiz: currentQuiz } })}
                    sx={{
                      borderRadius: 2,
                      px: 3,
                      py: 1.5,
                      fontWeight: 600,
                    }}
                  >
                    {t('quiz.edit')}
                  </Button>
                )}
              </Box>
            </Box>

            {completedAttempt && (
              <Alert 
                severity="info" 
                icon={<CheckCircle />}
                sx={{ 
                  mb: 2,
                  borderRadius: 2,
                }}
              >
                {t('quiz.alreadyCompleted')} {completedAttempt.completedAt && (
                  <Typography variant="body2" component="span" sx={{ ml: 1 }}>
                    {t('quiz.completedOn')} {new Date(completedAttempt.completedAt).toLocaleDateString()}
                  </Typography>
                )}
              </Alert>
            )}
          </Box>

          <Divider />

          {/* Questions Preview Section - Only show for tutors/creators or students who have completed the quiz */}
          {(isCreator || !isStudent || completedAttempt) && (
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <HelpOutline sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                  {t('quiz.questionsPreview')}
                </Typography>
              </Box>
              {currentQuiz.questions?.map((question, index) => (
                <Card 
                  key={index} 
                  elevation={0}
                  sx={{ 
                    mb: 2, 
                    p: 3,
                    borderRadius: 2,
                    border: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    bgcolor: alpha(theme.palette.primary.main, 0.02),
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Box
                      sx={{
                        minWidth: 32,
                        height: 32,
                        borderRadius: '50%',
                        bgcolor: theme.palette.primary.main,
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: '0.875rem',
                      }}
                    >
                      {index + 1}
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                        {question.question}
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Chip 
                          label={getQuestionTypeLabel(question.type, t)} 
                          size="small"
                          sx={{ 
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                            fontWeight: 600,
                          }}
                        />
                        <Chip 
                          label={`${question.points} ${t('quiz.points')}`} 
                          size="small"
                          color="primary"
                          sx={{ fontWeight: 600 }}
                        />
                      </Stack>
                    </Box>
                  </Box>
                </Card>
              ))}
            </CardContent>
          )}
          
          {/* Message for students who haven't taken the quiz yet */}
          {isStudent && !completedAttempt && (
            <CardContent sx={{ p: 4 }}>
              <Alert 
                severity="info" 
                icon={<HelpOutline />}
                sx={{ 
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.info.main, 0.1),
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {t('quiz.questionsHidden') || 'Questions are hidden until you complete the quiz'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('quiz.completeQuizToView') || 'Complete the quiz to view all questions and your results.'}
                </Typography>
              </Alert>
            </CardContent>
          )}

          {/* Quiz Results Section for Tutors */}
          {isCreator && (
            <>
              <Divider />
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Score sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                    <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                      {t('quiz.studentResults') || 'Student Results'}
                    </Typography>
                  </Box>
                  <Button
                    variant={showResults ? "outlined" : "contained"}
                    onClick={() => setShowResults(!showResults)}
                    sx={{
                      borderRadius: 2,
                      px: 3,
                      py: 1,
                      fontWeight: 600,
                    }}
                  >
                    {showResults ? (t('quiz.hideResults') || 'Hide Results') : (t('quiz.viewResults') || 'View Results')}
                  </Button>
                </Box>

                {showResults && (
                  <>
                    {loading && !quizResults ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <Loading />
                      </Box>
                    ) : quizResults ? (
                      <Box>
                        {/* Students with Results */}
                        {quizResults.studentsWithResults && quizResults.studentsWithResults.length > 0 && (
                          <Box sx={{ mb: 4 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: theme.palette.success.main }}>
                              {t('quiz.studentsCompleted') || 'Students Who Have Completed'} ({quizResults.studentsWithResults.length})
                            </Typography>
                            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}` }}>
                              <Table>
                                <TableHead>
                                  <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                                    <TableCell sx={{ fontWeight: 700 }}>{t('quiz.studentName') || 'Student Name'}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>{t('quiz.score') || 'Score'}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>{t('quiz.percentage') || 'Percentage'}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>{t('quiz.completedAt') || 'Completed At'}</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {quizResults.studentsWithResults.map((item) => (
                                    <TableRow key={item.studentId} hover>
                                      <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Person sx={{ fontSize: 20, color: theme.palette.primary.main }} />
                                          <Typography>
                                            {item.student?.userId?.firstName || ''} {item.student?.userId?.lastName || ''}
                                          </Typography>
                                        </Box>
                                      </TableCell>
                                      <TableCell align="right">
                                        <Chip
                                          label={`${item.attempt?.score || 0} / ${quizResults.quiz?.totalPoints || 0}`}
                                          color="primary"
                                          size="small"
                                          sx={{ fontWeight: 600 }}
                                        />
                                      </TableCell>
                                      <TableCell align="right">
                                        <Chip
                                          label={`${item.attempt?.percentage || 0}%`}
                                          color={item.attempt?.percentage >= 70 ? "success" : item.attempt?.percentage >= 50 ? "warning" : "error"}
                                          size="small"
                                          sx={{ fontWeight: 600 }}
                                        />
                                      </TableCell>
                                      <TableCell align="right">
                                        <Typography variant="body2" color="text.secondary">
                                          {item.attempt?.completedAt ? dayjs(item.attempt.completedAt).format('MMM DD, YYYY HH:mm') : '-'}
                                        </Typography>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Box>
                        )}

                        {/* Students without Results */}
                        {quizResults.studentsWithoutResults && quizResults.studentsWithoutResults.length > 0 && (
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: theme.palette.warning.main }}>
                              {t('quiz.studentsNotCompleted') || 'Students Who Haven\'t Completed'} ({quizResults.studentsWithoutResults.length})
                            </Typography>
                            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: `1px solid ${alpha(theme.palette.warning.main, 0.1)}` }}>
                              <Table>
                                <TableHead>
                                  <TableRow sx={{ bgcolor: alpha(theme.palette.warning.main, 0.05) }}>
                                    <TableCell sx={{ fontWeight: 700 }}>{t('quiz.studentName') || 'Student Name'}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>{t('quiz.status') || 'Status'}</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {quizResults.studentsWithoutResults.map((item) => (
                                    <TableRow key={item.studentId} hover>
                                      <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Person sx={{ fontSize: 20, color: theme.palette.warning.main }} />
                                          <Typography>
                                            {item.student?.userId?.firstName || ''} {item.student?.userId?.lastName || ''}
                                          </Typography>
                                        </Box>
                                      </TableCell>
                                      <TableCell align="right">
                                        <Chip
                                          icon={<Pending />}
                                          label={t('quiz.notCompleted') || 'Not Completed'}
                                          color="warning"
                                          size="small"
                                          sx={{ fontWeight: 600 }}
                                        />
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Box>
                        )}

                        {/* No students assigned */}
                        {(!quizResults.studentsWithResults || quizResults.studentsWithResults.length === 0) &&
                         (!quizResults.studentsWithoutResults || quizResults.studentsWithoutResults.length === 0) && (
                          <Alert severity="info" sx={{ borderRadius: 2 }}>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                              {t('quiz.noStudentsAssigned') || 'No students have been assigned to this quiz yet.'}
                            </Typography>
                          </Alert>
                        )}
                      </Box>
                    ) : (
                      <Alert severity="error" sx={{ borderRadius: 2 }}>
                        {t('quiz.failedToLoadResults') || 'Failed to load quiz results.'}
                      </Alert>
                    )}
                  </>
                )}
              </CardContent>
            </>
          )}
        </Card>
      </Container>
    </Box>
  );
};

export default QuizDetail;

